export interface StoreReconciliationAuditShiftModel {
  auditId: string;
  reportDate: string | null;
  reportDatetime: string | null;
  shiftStartDate: string | null;
  shiftStartTime: string | null;
  shiftEndDate: string | null;
  shiftEndTime: string | null;
  operatorName: string | null;
  userName: string | null;
  locationKey: string | null;
  locationName: string | null;
  locationType: string | null;
  isAutogenerated: boolean;
  ignorePosData: boolean;
  scratcherExpectedQty: number;
  scratcherExpectedSales: number;
  scratcherReportedQty: number;
  scratcherReportedSales: number;
  scratcherQtyVariance: number;
  scratcherSalesVariance: number;
  openingCount: number;
  openingPackCount: number;
  newlyActivatedCount: number;
  newlyActivatedPackCount: number;
  closingCount: number;
  closingPackCount: number;
  counterSoldOutPackCount: number;
  movedFromBackstockCount: number;
  movedFromBackstockTicketCount: number;
  movedToBackstockCount: number;
  movedToBackstockTicketCount: number;
}

const parseNumber = (value: unknown): number => {
  if (value == null || value === "") {
    return 0;
  }

  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

const parseStringOrNull = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  const nextValue = String(value).trim();
  return nextValue ? nextValue : null;
};

export const mapReconciliationAuditShift = (audit: any): StoreReconciliationAuditShiftModel => {
  const auditSummary = audit?.audit_summary ?? {};
  const posSummary = audit?.pos_summary ?? {};
  const scratcherVariance = audit?.reconciliation_variance?.scratcher ?? {};

  return {
    auditId: String(audit?.audit_id ?? audit?.id ?? ""),
    reportDate: parseStringOrNull(audit?.report_date ?? audit?.reportDate),
    reportDatetime: parseStringOrNull(audit?.report_datetime ?? audit?.audit_date ?? audit?.audit_created_at),
    shiftStartDate: parseStringOrNull(audit?.shift_start_date),
    shiftStartTime: parseStringOrNull(audit?.shift_start_time),
    shiftEndDate: parseStringOrNull(audit?.shift_end_date),
    shiftEndTime: parseStringOrNull(audit?.shift_end_time),
    operatorName: parseStringOrNull(audit?.operator_name),
    userName: parseStringOrNull(audit?.user_name),
    locationKey: parseStringOrNull(audit?.location_key),
    locationName: parseStringOrNull(audit?.location_name),
    locationType: parseStringOrNull(audit?.location_type),
    isAutogenerated: Boolean(audit?.is_autogenerated ?? audit?.isAutogenerated),
    ignorePosData: Boolean(audit?.ignore_pos_data ?? posSummary?.ignore_pos_data),
    scratcherExpectedQty: parseNumber(auditSummary?.total_sold ?? audit?.total_sold),
    scratcherExpectedSales: parseNumber(auditSummary?.total_revenue ?? auditSummary?.revenue ?? audit?.revenue),
    scratcherReportedQty: parseNumber(posSummary?.scratcher_count ?? posSummary?.scratcher_quantity_sold),
    scratcherReportedSales: parseNumber(posSummary?.scratcher_sales),
    scratcherQtyVariance: parseNumber(scratcherVariance?.quantity_variance),
    scratcherSalesVariance: parseNumber(scratcherVariance?.revenue_variance),
    openingCount: parseNumber(auditSummary?.opening_count ?? audit?.opening_count),
    openingPackCount: parseNumber(auditSummary?.opening_pack_count ?? audit?.opening_pack_count),
    newlyActivatedCount: parseNumber(auditSummary?.newly_activated_count ?? audit?.newly_activated_count),
    newlyActivatedPackCount: parseNumber(
      auditSummary?.newly_activated_pack_count ?? audit?.newly_activated_pack_count
    ),
    closingCount: parseNumber(auditSummary?.closing_count ?? audit?.closing_count),
    closingPackCount: parseNumber(auditSummary?.closing_pack_count ?? audit?.closing_pack_count),
    counterSoldOutPackCount: parseNumber(
      auditSummary?.counter_sold_out_pack_count ?? audit?.counter_sold_out_pack_count
    ),
    movedFromBackstockCount: parseNumber(
      auditSummary?.moved_from_backstock_count ?? audit?.moved_from_backstock_count
    ),
    movedFromBackstockTicketCount: parseNumber(
      auditSummary?.moved_from_backstock_ticket_count ?? audit?.moved_from_backstock_ticket_count
    ),
    movedToBackstockCount: parseNumber(
      auditSummary?.moved_to_backstock_after_audit_count
      ?? audit?.moved_to_backstock_after_audit_count
      ?? auditSummary?.moved_to_backstock_count
      ?? audit?.moved_to_backstock_count
    ),
    movedToBackstockTicketCount: parseNumber(
      auditSummary?.moved_to_backstock_after_audit_ticket_count
      ?? audit?.moved_to_backstock_after_audit_ticket_count
      ?? auditSummary?.moved_to_backstock_ticket_count
      ?? audit?.moved_to_backstock_ticket_count
    ),
  };
};

export default class StoreReconciliationAuditsByDateModel {
  private readonly _shifts: StoreReconciliationAuditShiftModel[];

  constructor(shifts: StoreReconciliationAuditShiftModel[]) {
    this._shifts = shifts;
  }

  get shifts(): StoreReconciliationAuditShiftModel[] {
    return this._shifts;
  }

  get count(): number {
    return this._shifts.length;
  }

  static fromServerData(data: unknown): StoreReconciliationAuditsByDateModel {
    const audits: unknown[] = Array.isArray(data) ? data : Array.isArray((data as any)?.audits) ? (data as any).audits : [];

    const shifts = audits
      .map(mapReconciliationAuditShift)
      .sort((a: StoreReconciliationAuditShiftModel, b: StoreReconciliationAuditShiftModel) => {
        const aTime = new Date(a.reportDatetime || 0).getTime();
        const bTime = new Date(b.reportDatetime || 0).getTime();
        return aTime - bTime;
      });

    return new StoreReconciliationAuditsByDateModel(shifts);
  }
}
